import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { Button } from '@eombor/ui';
import { t } from '../../shared/i18n/index.js';
import { PageHeader } from '../../shared/ui/PageHeader.js';
import { AddStaffDialog } from '../../features/staff/ui/AddStaffDialog.js';
import { StaffTable } from '../../features/staff/ui/StaffTable.js';
import { StaffPermissionsDialog } from '../../features/staff/ui/StaffPermissionsDialog.js';
import type { StaffDto } from '../../features/staff/api/staff.api.js';
import { rbacApi } from '../../features/rbac/api/rbac.api.js';
import { usePermission } from '../../features/auth/model/usePermission.js';
import { AccessSectionTabs } from '../../features/rbac/ui/AccessSectionTabs.js';
import { useScopeOptions } from '../useScopeOptions.js';

/**
 * Xodimlar va ularning rol biriktiruvlari.
 *
 * Jadval butun kenglikni oladi. Ilgari ekran ikki ustunli edi va o'ng ustun
 * xodim tanlanmaguncha bo'sh turardi — ekranning yarmi ko'p vaqt behuda
 * ketardi.
 *
 * Rollar ham, ruxsatlar ham OYNADA ochiladi. `UserAssignments` ning o'zida
 * rol qo'shish va olib tashlash oynalari bor — oyna ichida oyna `Esc`
 * bosilganda ikkalasini birdan yopardi, shuning uchun u `nested` rejimida
 * chaqiriladi va o'sha formalarni joyida, ichkarida chizadi.
 *
 * Doira obyektlari `useScopeOptions()` bilan yig'iladi: `features/rbac`
 * `features/org` ni ham, `features/warehouse` ni ham bilmaydi — ularni ilova
 * qatlami bog'laydi.
 */
export const AssignmentsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [permissionsFor, setPermissionsFor] = useState<StaffDto | null>(null);
  const [adding, setAdding] = useState(false);

  const { canAnywhere } = usePermission();
  const canAdd = canAnywhere('user.create');

  /*
   * Rollar oyna OCHILGANDA emas, sahifa bilan birga yuklanadi: ro'yxat
   * kichkina va o'zgarmaydi, oyna ichida kutish esa qo'shish oqimini
   * bo'lardi.
   */
  const roles = useQuery({
    queryKey: ['rbac', 'roles'],
    queryFn: () => rbacApi.listRoles(),
    enabled: canAdd,
  });

  const scopeOptions = useScopeOptions();

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('rbac.assignmentsTitle')}
        description={t('staff.pageHint')}
        actions={
          canAdd ? (
            <Button onClick={() => setAdding(true)}>
              <UserPlus size={16} aria-hidden />
              {t('staff.addButton')}
            </Button>
          ) : undefined
        }
      />

      <AccessSectionTabs
        view="staff"
        onChange={(view) => {
          if (view === 'roles') void navigate({ to: '/admin/roles' });
        }}
      />

      <StaffTable onShowPermissions={setPermissionsFor} scopeOptions={scopeOptions} />

      <AddStaffDialog
        open={adding}
        onClose={() => setAdding(false)}
        roles={roles.data?.items ?? []}
        scopeOptions={scopeOptions}
        // Yangi xodim ro'yxatda DARHOL ko'rinsin: oyna hali ochiq turganda
        // ham orqadagi jadval yangilanadi va odam "qo'shildimi?" deb
        // ikkinchi marta bosmaydi.
        onCreated={() => void queryClient.invalidateQueries({ queryKey: ['staff', 'list'] })}
      />

      <StaffPermissionsDialog
        staff={permissionsFor}
        scopeOptions={scopeOptions}
        onClose={() => setPermissionsFor(null)}
      />
    </div>
  );
};
